import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl } from '@angular/forms';
import {CaregiverService, ModalService} from '../../services';
import {TranslateService} from '@ngx-translate/core';

interface TimeSlot {
  id: number;
  startTime: string;
  endTime: string;
  isEditing?: boolean;
  editStartTime?: string;
  editEndTime?: string;
  editControl?: FormControl;
}

interface DaySchedule {
  day: string;
  dayIndex: number;
  timeSlots: TimeSlot[];
  isAddingNew: boolean;
  newStartTime: string;
  newEndTime: string;
  addControl?: FormControl;
}

@Component({
  selector: 'app-pop-up-hours',
  templateUrl: './pop-up-hours.component.html',
  styleUrls: ['./pop-up-hours.component.scss']
})
export class PopUpHoursComponent implements OnInit {
  days: DaySchedule[] = [
    { day: 'BACKEND.MONDAY', dayIndex: 1, timeSlots: [], isAddingNew: false, newStartTime: '', newEndTime: '', addControl: new FormControl(null) },
    { day: 'BACKEND.TUESDAY', dayIndex: 2, timeSlots: [], isAddingNew: false, newStartTime: '', newEndTime: '', addControl: new FormControl(null) },
    { day: 'BACKEND.WEDNESDAY', dayIndex: 3, timeSlots: [], isAddingNew: false, newStartTime: '', newEndTime: '', addControl: new FormControl(null) },
    { day: 'BACKEND.THURSDAY', dayIndex: 4, timeSlots: [], isAddingNew: false, newStartTime: '', newEndTime: '', addControl: new FormControl(null) },
    { day: 'BACKEND.FRIDAY', dayIndex: 5, timeSlots: [], isAddingNew: false, newStartTime: '', newEndTime: '', addControl: new FormControl(null) },
    { day: 'BACKEND.SATURDAY', dayIndex: 6, timeSlots: [], isAddingNew: false, newStartTime: '', newEndTime: '', addControl: new FormControl(null) },
    { day: 'BACKEND.SUNDAY', dayIndex: 0, timeSlots: [], isAddingNew: false, newStartTime: '', newEndTime: '', addControl: new FormControl(null) }
  ];

  private nextId = 1;
  private caregiver: any = null;
  @Input() id!: number;
  @Output() saved = new EventEmitter<void>();

  constructor(
    public activeModal: NgbActiveModal,
    public caregiverService: CaregiverService,
    private modalService: ModalService,
    public translate: TranslateService,
  ) {}

  ngOnInit() {
    this.getCaregiver(this.id);
  }

  private getCaregiver(id: number): void {
    this.caregiverService.getCaregiver(id)
      .subscribe(
        res => {
          this.caregiver = res.data;
          this.loadWorkHours();
        },
        () => this.modalService.open(this.translate.instant('BACKEND.COULDNT-GET-DATA'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-HOURS.POPUP.BACKEND-COULDNT-GET-DATA')
      );
  }

  private loadWorkHours(): void {
    this.days.forEach(day => day.timeSlots = []);

    if (!this.caregiver.workHours || !Array.isArray(this.caregiver.workHours)) {
      return;
    }

    this.caregiver.workHours.forEach((workHour: any) => {
      if (!workHour.enabled) {
        return;
      }

      const daySchedule = this.days.find(d => d.dayIndex === workHour.day);

      if (daySchedule) {
        const timeSlot: TimeSlot = {
          id: this.nextId++,
          startTime: workHour.startHour,
          endTime: workHour.endHour,
          isEditing: false,
          editStartTime: '',
          editEndTime: '',
          editControl: new FormControl(null)
        };

        daySchedule.timeSlots.push(timeSlot);
      }
    });
  }

  onAdd(dayIndex: number): void {
    this.days[dayIndex].isAddingNew = true;
    this.days[dayIndex].newStartTime = '';
    this.days[dayIndex].newEndTime = '';
    this.clearControl(this.days[dayIndex].addControl);
  }

  onNewRangeChange(dayIndex: number): void {
    const day = this.days[dayIndex];
    this.applyOverlapError(day.addControl, day.newStartTime, day.newEndTime, day.timeSlots);
  }

  onSave(dayIndex: number): void {
    const day = this.days[dayIndex];

    if (!day.newStartTime || !day.newEndTime) {
      this.modalService.open(this.translate.instant('COMMON.SELECT_BOTH_HOURS'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-HOURS.POPUP.COMMON-SELECT_BOTH_HOURS.ON-SAVE');
      return;
    }

    if (day.newStartTime >= day.newEndTime) {
      this.modalService.open(this.translate.instant('COMMON.END_AFTER_START'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-HOURS.POPUP.COMMON-END_AFTER_START.ON-SAVE');
      return;
    }

    if (this.hasOverlap(day.newStartTime, day.newEndTime, day.timeSlots)) {
      this.setOverlapError(day.addControl);
      return;
    }

    const newSlot: TimeSlot = {
      id: this.nextId++,
      startTime: day.newStartTime,
      endTime: day.newEndTime,
      isEditing: false,
      editStartTime: '',
      editEndTime: '',
      editControl: new FormControl(null)
    };

    day.timeSlots.push(newSlot);
    day.isAddingNew = false;
    day.newStartTime = '';
    day.newEndTime = '';
    this.clearControl(day.addControl);

    this.saveAllHours();

  }

  onCancel(dayIndex: number): void {
    this.days[dayIndex].isAddingNew = false;
    this.days[dayIndex].newStartTime = '';
    this.days[dayIndex].newEndTime = '';
    this.clearControl(this.days[dayIndex].addControl);
  }

  onEdit(dayIndex: number, slotId: number): void {
    const slot = this.days[dayIndex].timeSlots.find(s => s.id === slotId);
    if (slot) {
      slot.isEditing = true;
      slot.editStartTime = slot.startTime;
      slot.editEndTime = slot.endTime;
      this.clearControl(slot.editControl);
    }
  }

  onEditRangeChange(dayIndex: number, slotId: number): void {
    const day = this.days[dayIndex];
    const slot = day.timeSlots.find(s => s.id === slotId);
    if (!slot) return;
    const others = day.timeSlots.filter(s => s.id !== slotId);
    this.applyOverlapError(slot.editControl, slot.editStartTime, slot.editEndTime, others);
  }

  onSaveEdit(dayIndex: number, slotId: number): void {
    const day = this.days[dayIndex];
    const slot = day.timeSlots.find(s => s.id === slotId);

    if (!slot) return;

    if (!slot.editStartTime || !slot.editEndTime) {
      this.modalService.open(this.translate.instant('COMMON.SELECT_BOTH_HOURS'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-HOURS.POPUP.COMMON-SELECT_BOTH_HOURS.ON-SAVE-EDIT');
      return;
    }

    if (slot.editStartTime >= slot.editEndTime) {
      this.modalService.open(this.translate.instant('COMMON.END_AFTER_START'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-HOURS.POPUP.COMMON-END_AFTER_START.ON-SAVE-EDIT');
      return;
    }

    const others = day.timeSlots.filter(s => s.id !== slotId);
    if (this.hasOverlap(slot.editStartTime, slot.editEndTime, others)) {
      this.setOverlapError(slot.editControl);
      return;
    }

    slot.startTime = slot.editStartTime;
    slot.endTime = slot.editEndTime;
    slot.isEditing = false;
    slot.editStartTime = '';
    slot.editEndTime = '';
    this.clearControl(slot.editControl);

    this.saveAllHours();
  }


  onCancelEdit(dayIndex: number, slotId: number): void {
    const slot = this.days[dayIndex].timeSlots.find(s => s.id === slotId);
    if (slot) {
      slot.isEditing = false;
      slot.editStartTime = '';
      slot.editEndTime = '';
      this.clearControl(slot.editControl);
    }
  }

  // Dwa przedzialy w tym samym dniu nie moga nachodzic na siebie ani sie stykac
  // (np. 01:00-12:00 i 12:00-13:00 sa traktowane jako nachodzace na siebie).
  private hasOverlap(start: string, end: string, slots: TimeSlot[]): boolean {
    if (!start || !end || start >= end) {
      return false;
    }
    return slots.some(s => start <= s.endTime && s.startTime <= end);
  }

  private applyOverlapError(control: FormControl, start: string, end: string, slots: TimeSlot[]): void {
    if (!control) {
      return;
    }
    if (this.hasOverlap(start, end, slots)) {
      this.setOverlapError(control);
    } else {
      this.clearControl(control);
    }
  }

  private setOverlapError(control: FormControl): void {
    if (!control) {
      return;
    }
    control.setErrors({ overlappingHours: true });
    control.markAsTouched();
  }

  private clearControl(control: FormControl): void {
    if (!control) {
      return;
    }
    control.setErrors(null);
  }

  onDelete(dayIndex: number, slotId: number): void {
    this.days[dayIndex].timeSlots = this.days[dayIndex].timeSlots.filter(
      slot => slot.id !== slotId
    );

    this.saveAllHours();
  }

  saveAllHours(): void {
    const workHours: any[] = [];

    this.days.forEach(day => {
      day.timeSlots.forEach(slot => {
        workHours.push({
          day: day.dayIndex,
          enabled: true,
          startHour: slot.startTime,
          endHour: slot.endTime
        });
      });
    });

    const credentials = {
      caregiver: this.caregiver.id,
      workHours: workHours
    };

    this.caregiverService
      .workHours(credentials)
      .subscribe(
        () => {
        },
        err => {
          this.getCaregiver(this.caregiver.id);

          this.modalService.open(this.translate.instant('COMMON.WORK_HOURS_SAVE_ERROR'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-HOURS.POPUP.COMMON-WORK_HOURS_SAVE_ERROR');
        }
      );
  }

  onCloseModal() {
    this.saved.emit();
    this.activeModal.close();
  }

}
